import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { useScrollLock } from '@dustin-web/microsite-components/src/hooks'
import { useGraphqlMutation } from '@dustin-web/microsite-data/src/hooks'
import {
  DesktopStateActionType,
  MobileStateActionType,
  useHeaderContext,
} from './../header-context'
import InstantSearchMiniSkeleton from './skeleton'
import InstantSearchMiniLayout from './layout'
import type { GetAutocompleteE4Query } from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import { NOTIFY_REMOVE_RECENTLY_VIEWED } from '@dustin-web/microsite-data/src/graphql/product/notify-remove-recently-viewed.graphql'
import { NOTIFY_REMOVE_RECENT_SEARCHES } from '@dustin-web/microsite-data/src/graphql/product/notify-remove-recent-searches.graphql'
import {
  NotifyRemoveRecentSearchesMutationVariables,
  NotifyRemoveRecentlyViewedMutationVariables,
  NotifyRemoveRecentlyViewedMutation,
  NotifyRemoveRecentSearchesMutation,
} from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'

type Props = {
  searchResult: GetAutocompleteE4Query | undefined
  searchPhrase: string
  isLoading: boolean
  isFocused: boolean
}

const InstantSearchMini = ({ isFocused, searchPhrase, searchResult, isLoading }: Props) => {
  const {
    products,
    recentlyViewed = [],
    suggestions,
    recentSearches = [],
    relatedCategories = [],
  } = searchResult?.autoComplete ?? {}
  const [stateRecentlyViewed, setStateRecentlyViewed] = useState(recentlyViewed)
  const [stateRecentSearches, setStateRecentSearches] = useState(recentSearches)
  const { dispatchDesktop, dispatchMobile } = useHeaderContext()

  const initiateSearchOrSearchPhrase = !!searchPhrase

  const [notifyRemoveRecentSearches] = useGraphqlMutation<
    NotifyRemoveRecentSearchesMutation,
    NotifyRemoveRecentSearchesMutationVariables
  >(NOTIFY_REMOVE_RECENT_SEARCHES)
  const [notifyRemoveRecentlyViewed] = useGraphqlMutation<
    NotifyRemoveRecentlyViewedMutation,
    NotifyRemoveRecentlyViewedMutationVariables
  >(NOTIFY_REMOVE_RECENTLY_VIEWED)

  useScrollLock(initiateSearchOrSearchPhrase && (isLoading || isFocused))

  useEffect(() => {
    if (recentlyViewed?.length) {
      setStateRecentlyViewed(recentlyViewed.slice(0, 3))
    }
  }, [recentlyViewed])

  useEffect(() => {
    if (recentSearches?.length) {
      setStateRecentSearches(recentSearches.slice(0, 3))
    }
  }, [recentSearches])

  useEffect(() => {
    const visible = initiateSearchOrSearchPhrase && (isLoading || isFocused)

    visible &&
      dispatchDesktop({
        type: DesktopStateActionType.SET_ACTIVE_MEGA_MENU,
        payload: {
          activeMegaMenu: '',
        },
      })

    dispatchMobile({
      type: visible ? MobileStateActionType.SHOW_SEARCH : MobileStateActionType.HIDE_SEARCH,
    })

    dispatchDesktop({
      type: DesktopStateActionType.SET_MENU_ACTIVE,
      payload: {
        menu: {
          name: 'search',
          active: visible,
        },
      },
    })
  }, [initiateSearchOrSearchPhrase, isFocused, dispatchDesktop, isLoading, dispatchMobile])

  const clearRecentSearchesHandler = async () => {
    setStateRecentSearches([])
    await notifyRemoveRecentSearches({
      variables: {
        phrase: '',
        removeAll: true,
      },
    })
  }
  const clearRecentSearchHandler = async (title?: string | null) => {
    if (title) {
      setStateRecentSearches(stateRecentSearches?.filter(item => item?.title !== title) || [])
      await notifyRemoveRecentSearches({
        variables: {
          phrase: title,
          removeAll: false,
        },
      })
    }
  }

  const clearRecentViewedHandler = async () => {
    setStateRecentlyViewed([])
    await notifyRemoveRecentlyViewed({
      variables: {
        productErpIdentifier: '',
        removeAll: true,
      },
    })
  }

  const layoutProps = {
    products: products?.slice(0, 3),
    suggestions: suggestions?.slice(0, 3),
    relatedCategories: relatedCategories?.slice(0, 3),
  }

  return (
    <div className="c-header__autocomplete-mini-wrap">
      <div
        className={classNames('c-header__autocomplete-mini u-slim-dustin-scrollbar', {
          'c-header__autocomplete-mini--active':
            initiateSearchOrSearchPhrase && (isLoading || isFocused),
        })}>
        {isLoading ? (
          <InstantSearchMiniSkeleton />
        ) : (
          <InstantSearchMiniLayout
            searchPhrase={searchPhrase}
            relatedCategories={layoutProps.relatedCategories}
            suggestions={layoutProps.suggestions}
            recentSearches={stateRecentSearches}
            products={layoutProps.products}
            recentlyViewed={stateRecentlyViewed}
            clearRecentSearchesHandler={clearRecentSearchesHandler}
            clearRecentSearchHandler={clearRecentSearchHandler}
            clearRecentViewedHandler={clearRecentViewedHandler}
          />
        )}
      </div>
    </div>
  )
}

export default InstantSearchMini
